import { AAROH_IMAGE_DIMENSIONS, AAROH_IMAGES } from "./images.js";

export const SITE_URL = "https://projectgrid.org";

export const ORGANIZATION_NAME = "Project GRID";
export const ORGANIZATION_DESCRIPTION =
  "A youth-led innovation ecosystem helping students investigate real-world problems and build practical responses.";
export const ORGANIZATION_EMAIL = "founder@projectgrid.org";

export const ORGANIZATION_LOGO = {
  src: "/images/brand/project-grid-organization-logo-1024.png",
  width: 1024,
  height: 1024,
  alt: "Project GRID organization logo",
};

export const DEFAULT_SEO_IMAGE = {
  src: AAROH_IMAGES.roomWide,
  width: 1800,
  height: 1012,
  alt: "AAROH participants building with laptops across a full auditorium",
};

export const ORGANIZATION_SAME_AS = [
  "https://www.linkedin.com/company/project-grid-global",
  "https://www.instagram.com/projectgrid_org/",
];

export function organizationGraph() {
  return [
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: ORGANIZATION_NAME,
      url: `${SITE_URL}/`,
      logo: {
        "@type": "ImageObject",
        "@id": `${SITE_URL}/#logo`,
        url: `${SITE_URL}${ORGANIZATION_LOGO.src}`,
        contentUrl: `${SITE_URL}${ORGANIZATION_LOGO.src}`,
        width: ORGANIZATION_LOGO.width,
        height: ORGANIZATION_LOGO.height,
        caption: ORGANIZATION_LOGO.alt,
      },
      image: [
        `${SITE_URL}${AAROH_IMAGES.roomWide}`,
        `${SITE_URL}${AAROH_IMAGES.buildSession}`,
        `${SITE_URL}/images/grid-photo-09-p8.jpg`,
        `${SITE_URL}/images/campaign/grid-campaign-11-aurora-winners.jpg`,
      ],
      description: ORGANIZATION_DESCRIPTION,
      email: ORGANIZATION_EMAIL,
      founder: [
        {
          "@type": "Person",
          name: "Moksh Sindhwani",
          url: `${SITE_URL}/team`,
        },
        {
          "@type": "Person",
          name: "Tanishq Aggarwal",
          url: `${SITE_URL}/team`,
        },
      ],
      sameAs: ORGANIZATION_SAME_AS,
    },
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      url: `${SITE_URL}/`,
      name: ORGANIZATION_NAME,
      description: ORGANIZATION_DESCRIPTION,
      inLanguage: "en-IN",
      publisher: { "@id": `${SITE_URL}/#organization` },
    },
  ];
}

const CAMPAIGN_DIMENSIONS = {
  "grid-campaign-03-makerspace": [1000, 625],
  "grid-people-we-met-hero-v1": [1400, 788],
};

const GRID_PHOTO_DIMENSIONS = {
  "grid-photo-01-p1": [1800, 1202],
  "grid-photo-02-p7": [1800, 1202],
  "grid-photo-03-p7": [1800, 1202],
  "grid-photo-04-p7": [1800, 1202],
  "grid-photo-05-p7": [1800, 1202],
  "grid-photo-06-p7": [1800, 1202],
  "grid-photo-07-p7": [1800, 1202],
  "grid-photo-08-p7": [1800, 1202],
  "grid-photo-09-p8": [1800, 1202],
  "grid-photo-10-p8": [960, 1280],
  "grid-photo-11-p8": [960, 1280],
};

const PEOPLE_MET_DIMENSIONS = {
  DamsytGmWmH: [1080, 810],
  Damu65hSuq4: [1080, 1440],
  DamuPaKSA0J: [1080, 566],
  DamurCcyX6R: [1080, 1080],
  DamvNL9yFYV: [1080, 810],
};

const EVIDENCE_DIMENSIONS = {
  "aurora-30-countries": [800, 200],
  "aurora-top20-team": [800, 565],
  "aurora-winner-01": [800, 449],
  "aurora-winner-02": [800, 565],
  "aurora-wrap": [538, 306],
  "gantavya-project": [960, 1280],
  "innovatex-programme-card": [800, 336],
  "jerovin-chillgui-02": [960, 1280],
};

const OTHER_IMAGE_DIMENSIONS = {
  "/images/aaroh/optimized/aaroh-student-collage-hero-v1.avif": [1400, 788],
  "/images/aurora/aurora-devpost-background.jpg": [1920, 1080],
  "/images/aurora/aurora-devpost-cover.jpg": [600, 300],
};

export function getImageData(src) {
  if (!src) return null;

  const aarohMatch = src.match(/^\/images\/aaroh\/archive\/(aaroh-[a-z-]+)-1800\.webp$/);
  if (aarohMatch) {
    const name = aarohMatch[1];
    const [width, height] = AAROH_IMAGE_DIMENSIONS[name];
    return {
      src,
      width,
      height,
      sources: [
        {
          type: "image/webp",
          srcSet: `/images/aaroh/archive/${name}-640.webp 640w, /images/aaroh/archive/${name}-1200.webp 1200w, ${src} 1800w`,
        },
      ],
    };
  }

  const campaignMatch = src.match(
    /^\/images\/campaign\/(?:optimized\/)?(grid-campaign-\d{2}-[a-z-]+|grid-people-we-met-hero-v1)\.(?:avif|jpg|png)$/,
  );
  if (campaignMatch) {
    const name = campaignMatch[1];
    const [width, height] = CAMPAIGN_DIMENSIONS[name] ?? [1000, 666];
    const avif = `/images/campaign/optimized/${name}.avif`;
    return {
      src,
      width,
      height,
      sources: [
        {
          type: "image/avif",
          srcSet: `/images/campaign/optimized/${name}-640.avif 640w, ${avif} ${width}w`,
        },
      ],
    };
  }

  const gridPhotoMatch = src.match(/^\/images\/(grid-photo-\d{2}-p\d+)\.jpg$/);
  if (gridPhotoMatch) {
    const name = gridPhotoMatch[1];
    const [width, height] = GRID_PHOTO_DIMENSIONS[name];
    const largeWidth = Math.min(width, 1200);
    return {
      src,
      width,
      height,
      sources: [
        {
          type: "image/webp",
          srcSet: `/images/optimized/grid-webp/${name}-640.webp 640w, /images/optimized/grid-webp/${name}-${largeWidth}.webp ${largeWidth}w`,
        },
      ],
    };
  }

  const peopleMatch = src.match(/^\/images\/people-met\/([A-Za-z0-9]+)\.jpg$/);
  if (peopleMatch) {
    const name = peopleMatch[1];
    const [width, height] = PEOPLE_MET_DIMENSIONS[name];
    return {
      src,
      width,
      height,
      sources: [
        {
          type: "image/avif",
          srcSet: `/images/optimized/people-met/${name}-640.avif 640w, /images/optimized/people-met/${name}-${width}.avif ${width}w`,
        },
      ],
    };
  }

  const evidenceMatch = src.match(/^\/images\/evidence\/([a-z0-9-]+)\.jpg$/);
  if (evidenceMatch) {
    const name = evidenceMatch[1];
    const [width, height] = EVIDENCE_DIMENSIONS[name];
    const smallWidth = Math.min(width, 640);
    const srcSet =
      width > smallWidth
        ? `/images/optimized/evidence/${name}-${smallWidth}.avif ${smallWidth}w, /images/optimized/evidence/${name}-${width}.avif ${width}w`
        : `/images/optimized/evidence/${name}-${width}.avif ${width}w`;
    return {
      src,
      width,
      height,
      sources: [{ type: "image/avif", srcSet }],
    };
  }

  const dimensions = OTHER_IMAGE_DIMENSIONS[src];
  if (dimensions) {
    const [width, height] = dimensions;
    if (src.includes("/optimized/") && src.endsWith(".avif")) {
      return {
        src,
        width,
        height,
        sources: [
          {
            type: "image/avif",
            srcSet: `${src.replace(/\.avif$/, "-640.avif")} 640w, ${src} ${width}w`,
          },
        ],
      };
    }
    return { src, width, height, sources: [] };
  }

  return null;
}

export const MEDIA_LIBRARY = [
  {
    id: "community",
    src: AAROH_IMAGES.roomWide,
    fallback: AAROH_IMAGES.roomWide,
    width: 1800,
    height: 1012,
    alt: "AAROH participants building with laptops across a full auditorium",
    caption: "The AAROH 2026 build room in motion at IIIT Delhi.",
    href: "/programmes/aaroh",
    linkLabel: "Explore AAROH",
  },
  {
    id: "founders",
    src: "/images/grid-photo-09-p8.jpg",
    fallback: "/images/grid-photo-09-p8.jpg",
    width: 1800,
    height: 1202,
    alt: "Three student builders together in a workshop space",
    caption: "Student builders together inside the programme space.",
    href: "/team",
    linkLabel: "Meet the Project GRID team",
  },
  {
    id: "makerspace",
    src: "/images/grid-photo-08-p7.jpg",
    fallback: "/images/grid-photo-08-p7.jpg",
    width: 1800,
    height: 1202,
    alt: "Students visiting an active fabrication workshop",
    caption: "Students learning inside an active fabrication workshop.",
    href: "/programmes",
    linkLabel: "Explore Project GRID programmes",
  },
  {
    id: "speaker",
    src: AAROH_IMAGES.studentSpeaker,
    fallback: AAROH_IMAGES.studentSpeaker,
    width: 1800,
    height: 1200,
    alt: "A student speaking with a microphone during AAROH at IIIT Delhi",
    caption: "A student voice in the room at AAROH 2026.",
    href: "/programmes/aaroh",
    linkLabel: "See the AAROH programme",
  },
  {
    id: "cohort",
    src: AAROH_IMAGES.room,
    fallback: AAROH_IMAGES.room,
    width: 1800,
    height: 1012,
    alt: "Students working in teams across the AAROH auditorium at IIIT Delhi",
    caption: "Teams working across the room during AAROH 2026.",
    href: "/impact",
    linkLabel: "See documented impact",
  },
  {
    id: "aurora-winners",
    src: "/images/campaign/optimized/grid-campaign-11-aurora-winners.avif",
    fallback: "/images/campaign/grid-campaign-11-aurora-winners.jpg",
    width: 1000,
    height: 666,
    alt: "Aurora Global Hackathon winners holding laptop prizes in a Project GRID campaign collage",
    caption: "Aurora Global Hackathon winners with their awarded laptop prizes.",
    href: "/programmes/aurora",
    linkLabel: "See the Aurora programme",
  },
  {
    id: "builders",
    src: AAROH_IMAGES.buildSession,
    fallback: AAROH_IMAGES.buildSession,
    width: 1800,
    height: 1200,
    alt: "A mentor and students working together on laptops during AAROH",
    caption: "A team testing ideas with support during AAROH 2026.",
    href: "/projects",
    linkLabel: "Explore student projects",
  },
  {
    id: "partnerships",
    src: AAROH_IMAGES.collaboration,
    fallback: AAROH_IMAGES.collaboration,
    width: 1800,
    height: 1012,
    alt: "AAROH collaborators and participants holding an award together on stage",
    caption: "AAROH participants and collaborators at the closing showcase.",
    href: "/partnerships",
    linkLabel: "See our partnerships",
  },
];

export const ROUTE_SEO_IMAGES = {
  "/": DEFAULT_SEO_IMAGE,
  "/about": {
    src: "/images/grid-photo-09-p8.jpg",
    width: 1800,
    height: 1202,
    alt: "Three student builders together in a workshop space",
  },
  "/team": {
    src: "/images/grid-photo-09-p8.jpg",
    width: 1800,
    height: 1202,
    alt: "Three student builders together in a workshop space",
  },
  "/partnerships": {
    src: AAROH_IMAGES.collaboration,
    width: 1800,
    height: 1012,
    alt: "AAROH collaborators and participants holding an award together on stage",
  },
  "/journey": {
    src: AAROH_IMAGES.roomWide,
    width: 1800,
    height: 1012,
    alt: "AAROH participants building with laptops across a full auditorium",
  },
  "/impact": {
    src: AAROH_IMAGES.roomWide,
    width: 1800,
    height: 1012,
    alt: "AAROH participants building with laptops across a full auditorium",
  },
  "/governance": DEFAULT_SEO_IMAGE,
  "/programmes": {
    src: AAROH_IMAGES.room,
    width: 1800,
    height: 1012,
    alt: "Students working in teams across the AAROH auditorium at IIIT Delhi",
  },
  "/programmes/aaroh": {
    src: AAROH_IMAGES.room,
    width: 1800,
    height: 1012,
    alt: "Students working in teams across the AAROH auditorium at IIIT Delhi",
  },
  "/programmes/aurora": {
    src: "/images/campaign/grid-campaign-11-aurora-winners.jpg",
    width: 1536,
    height: 1024,
    alt: "Aurora Global Hackathon winners holding their laptop prizes",
  },
  "/programmes/metamorphosis": {
    src: "/images/grid-photo-04-p7.jpg",
    width: 1800,
    height: 1202,
    alt: "A large student cohort gathered in an auditorium",
  },
  "/media": DEFAULT_SEO_IMAGE,
  "/projects": {
    src: AAROH_IMAGES.buildSession,
    width: 1800,
    height: 1200,
    alt: "A mentor and students working together on laptops during AAROH",
  },
};

export function imageForRoute(route) {
  return ROUTE_SEO_IMAGES[route] ?? DEFAULT_SEO_IMAGE;
}
